import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, Facebook, Instagram, ExternalLink, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";
import { SOCIAL } from "../../lib/social";
import QuickPhotoButton from "./QuickPhotoButton";

/**
 * FeaturedReelStrip — Landing tile pointing at the operator's latest
 * social reel (Facebook / Instagram / TikTok — anything with a URL).
 *
 * Reads /api/featured-reel. Auto-hides when disabled or the API errors,
 * so a blank slot never appears on the homepage.
 *
 * The tile is deliberately loud: dark card, orange CTA, giant play-icon
 * so it grabs the eye between the hero and the search widget.
 */
export default function FeaturedReelStrip() {
  const [reel, setReel] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/featured-reel")
      .then(({ data }) => { if (alive) setReel(data); })
      .catch(() => {})
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  if (!ready || !reel || !reel.enabled || !reel.video_url) return null;

  const platform = (reel.platform || "Facebook").trim();
  const cta = `Watch on ${platform}`;
  const igHandle = (SOCIAL?.instagram?.handle || "campcompassuk").replace(/^@/, "");
  const fbHandle = (SOCIAL?.facebook?.handle || "campcompassuk").replace(/^@/, "");
  const igUrl = `https://instagram.com/${igHandle}`;
  const fbUrl = `https://facebook.com/${fbHandle}`;

  return (
    <section
      className="bg-[#0e0e0e] text-white border-y hairline-strong"
      data-testid="featured-reel-strip"
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-10 py-3 sm:py-4">
        <div className="grid md:grid-cols-[minmax(120px,140px)_1fr] gap-4 items-center">
          {/* Thumbnail — tighter aspect on desktop (max-h=260) so the whole
              strip stays around ~35% viewport height, not half the page. */}
          <a
            href={reel.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block relative aspect-[9/16] max-h-[180px] mx-auto md:mx-0 overflow-hidden bg-[#1a1a1a] group hover:opacity-95 transition"
            data-testid="featured-reel-thumbnail"
          >
            {reel.thumbnail_data_url ? (
              <img
                src={reel.thumbnail_data_url}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#ff4500] via-[#e63e00] to-[#0e0e0e]" />
            )}
            <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition" />
            <div className="absolute inset-0 flex items-center justify-center">
              <PlayCircle className="w-16 h-16 sm:w-20 sm:h-20 text-white drop-shadow-lg" strokeWidth={1.4} />
            </div>
            <div className="absolute top-2 left-2 mono text-[9px] tracking-widest font-bold uppercase bg-[#ff4500] text-white px-1.5 py-0.5">
              New · {platform}
            </div>
          </a>

          {/* Copy + CTAs */}
          <div>
            <div className="eyebrow text-[#ff4500] mb-1">🎬 Fresh from us</div>
            <h2 className="font-display text-2xl sm:text-3xl leading-tight mb-2" data-testid="featured-reel-title">
              {reel.title || "Watch our latest reel"}
            </h2>
            {reel.caption && (
              <p className="text-sm text-white/80 mb-4 max-w-xl" data-testid="featured-reel-caption">
                {reel.caption}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <a
                href={reel.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs font-bold uppercase tracking-wider shadow-sm transition-colors"
                data-testid="featured-reel-cta"
              >
                <PlayCircle className="w-4 h-4" /> {cta} <ExternalLink className="w-3 h-3 opacity-80" />
              </a>
              <a
                href={fbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider transition-colors"
                data-testid="featured-reel-follow-fb"
              >
                <Facebook className="w-4 h-4" /> Follow us
              </a>
              <a
                href={igUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider transition-colors"
                data-testid="featured-reel-follow-ig"
              >
                <Instagram className="w-4 h-4" /> Instagram
              </a>
            </div>
            <p className="text-[11px] text-white/50 mt-3">
              A tap opens the reel in a new tab · give us a follow while you&apos;re there 🏕
            </p>

            {/* iter138 — Reciprocity nudge: after they've watched OUR reel,
                the community wall is the natural next click. This block is
                deliberately visually distinct (thin orange left-border) so
                it reads as a separate call-to-action, not just another
                CTA in the row above. */}
            <div className="mt-5 pl-3 border-l-2 border-[#ff4500]" data-testid="featured-reel-community-nudge">
              <div className="eyebrow text-[#ff4500] mb-1">Your turn 🏕</div>
              <p className="text-sm text-white/90 font-medium mb-2">
                Now you&apos;ve seen ours — share yours.
              </p>
              <p className="text-xs text-white/60 mb-3 max-w-md">
                Post a photo, a review or a trip story to the community wall.
                Other campers get the honest take, you get the credit.
              </p>
              <Link
                to="/wall"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-[#0e0e0e] hover:bg-[#f5f5f5] text-xs font-bold uppercase tracking-wider transition-colors"
                data-testid="featured-reel-share-yours"
              >
                <MessageCircle className="w-4 h-4" /> Share your trip
              </Link>
              {/* iter139 — Quick-photo shortcut: skips the full trip form
                  for campers who just want to drop a shot. Sits next to
                  the trip button so the two options are visually paired. */}
              <QuickPhotoButton className="inline-flex items-center gap-1.5 ml-2 px-4 py-2.5 rounded-full border border-white/40 hover:border-white text-white text-xs font-bold uppercase tracking-wider transition-colors" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
